<script lang="ts" setup>
import { RoleEnum } from "~/types/User";
import { useWindowSize } from '@vueuse/core'
import { useNavTabs, type TabItem } from "~/composables/useNavTabs";
import Header from '~/components/layouts/Header.vue'
import Footer from '~/components/layouts/Footer.vue'
import BottomBar from '~/components/custom-ui/BottomBar.vue'
import UserProfileCard from "~/components/pages/office/UserProfileCard.vue";
import MasterModeSwitcher from "~/components/pages/office/MasterModeSwitcher.vue";
import type {ResponseType} from "~/types/Task";
import type {MetaType} from "~/types/Meta";
import {endpoints} from "~/constants/endpoints";
import { Button } from '~/components/ui/button'
import RoomsList from '~/components/pages/office/chat/RoomsList.vue'

const navTabs = useNavTabs()
const userStore = useUserStore();
const route = useRoute()
const { width } = useWindowSize()
const router = useRouter()
const loading = ref(false)

const tabList = navTabs.value.filter(item => item.role.includes(userStore.currentRole as RoleEnum))
const isCurrentRoute = (item: TabItem) => {
    return route.path === item.path || item.child_pages.includes(route.path)
}

const chatList = ref<ResponseType[]>([])
const totalChats = ref(0)
const pagination = ref({
    page: 1,
    pageSize: 20,
})
const isLoadingMore = ref(false)
const hasMoreChats = computed(() => chatList.value.length < totalChats.value)

async function getChats(isLoadMore = false){
    if(isLoadMore) {
        isLoadingMore.value = true
    } else {
        loading.value = true
    }
    
    await nextTick()
    /* let endpoint = endpoints.chat.client_chats
    if(userStore.user?.user_role === RoleEnum.MASTER){
        endpoint = endpoints.chat.master_chats
    } */
    
    const result = await fetchAuth<ResponseType[]>(endpoints.chat.responses, {
        method: 'GET',
        query: {
            'pagination[page]': pagination.value.page,
            'pagination[pageSize]': pagination.value.pageSize,
            'filters[role]': userStore.user?.user_role === RoleEnum.MASTER ? 'master' : 'customer',
        }
    })
    
    try {
        const {data, meta} = result
        if(!data || !Array.isArray(data)) return []
        
        const filteredChats = data.filter((chat: ResponseType) => !!chat.task).map((chat: ResponseType) => {
            const chatWithMessages = chat as any
            chat.timestamp = chatWithMessages.messages?.[0]?.createdAt 
                ? new Date(chatWithMessages.messages[0].createdAt).getTime() 
                : 0
            return chat
        })
        
        const metaTyped = meta as MetaType | null
        if(metaTyped?.pagination?.total) {
            totalChats.value = metaTyped.pagination.total
        }
        
        if(isLoadMore) {
            chatList.value = [...chatList.value, ...filteredChats]
        } else {
            chatList.value = filteredChats
        }
        
        return filteredChats
    } catch (e) {
        console.error(e)
        return []
    } finally {
        loading.value = false
        isLoadingMore.value = false
    }
}

async function loadMoreChats() {
    if(isLoadingMore.value || !hasMoreChats.value) return
    
    pagination.value.page += 1
    await getChats(true)
}

await useAsyncData('chat_list', () => getChats())

const sortedChats = computed(() => {
    if(!chatList.value) return []
    return chatList.value.sort((a: ResponseType, b: ResponseType) => (b?.timestamp || 0) - (a?.timestamp || 0))
})

const chatState = ref<any[]>([])

const isMobile = computed(() => {
    return width.value <= 768
})

const {
    isConnected,
    onUsersOnline,
    subscribeToUsers,
    connect,
    usersOnline
} = useSocket()

const userIdsFromChats = computed(() => {
    if (!chatList.value || !userStore.user?.documentId) return []
    
    const userIds = new Set<string>()
    
    chatList.value.forEach((chat: ResponseType) => {
        if (userStore.user?.user_role === RoleEnum.MASTER) {

            if (chat.task?.owner?.documentId && chat.task.owner.documentId !== userStore.user.documentId) {
                userIds.add(chat.task.owner.documentId)
            }
        } else if (userStore.user?.user_role === RoleEnum.CUSTOMER) {
            if (chat.portfolio?.user?.documentId && chat.portfolio.user.documentId !== userStore.user.documentId) {
                userIds.add(chat.portfolio.user.documentId)
            }
        }
    })
    
    return Array.from(userIds).sort()
})

// Отправляем подписку на пользователей
const sendUserSubscription = () => {
    if (!isConnected.value) {
        console.warn('Socket is not connected. Cannot subscribe to users.')
        return
    }
    
    if (userIdsFromChats.value.length > 0) {
        subscribeToUsers(userIdsFromChats.value)
    }
}

watch(isConnected, (newIsConnected) => {
    if(newIsConnected){
        sendUserSubscription()
    }
})

const initializeChat = async () => {
    if (!userStore?.access_token) return;
    
    if (!isConnected.value) {
        connect(userStore.access_token)
        await new Promise<void>((resolve) => {
            const checkConnection = setInterval(() => {
                if (isConnected.value) {
                    clearInterval(checkConnection)
                    resolve()
                }
            }, 100)
            
            setTimeout(() => {
                clearInterval(checkConnection)
                resolve()
            }, 5000)
        })
    }
    
    return setupSocketConnection();
}

const setupSocketConnection = () => {
    if(!isConnected.value) return;

    const removeUsersOnlineHandler = onUsersOnline((users: any[]) => {
        // Тут можно воткнуть кастомную логику, на будущее
    })

    sendUserSubscription()

    return () => {
        removeUsersOnlineHandler()
    }
}

let cleanup: (() => void) | undefined

watch(userIdsFromChats, (val, oldVal) => {
    if (isConnected.value && val.length > 0 && JSON.stringify(val) !== JSON.stringify(oldVal)) {
        sendUserSubscription()
    }
}, { deep: true })

onMounted(async () => {
    cleanup = await initializeChat()
})

onUnmounted(() => {
    if (cleanup) {
        cleanup()
    }
})

watch(chatState, (val) => {
    if(val.length > 0){
        val.forEach((wsChat) => {
            if(!wsChat?.last_message?.createdAt) return
            setNewTimestamp(wsChat.documentId, wsChat.last_message.createdAt)
        })
    }
})

function setNewTimestamp(documentId: string, date: string){
    if(!chatList.value) return
    const chatIndex = chatList.value.findIndex((item) => item.documentId === documentId)
    if(chatIndex < 0) return
    const newTimestamp = new Date(date).getTime()
    if(newTimestamp > (chatList.value[chatIndex]?.timestamp || 0)){
        chatList.value[chatIndex].timestamp = newTimestamp
    }
}
const chatContainer = ref<HTMLElement | null>(null)
provide('chatState', chatState)
provide('setNewTimestamp', setNewTimestamp)
provide('chatContainer', chatContainer)

function setViewedMessages(documentId: string){
    const chatIndex = chatList.value.findIndex((item) => item.documentId === documentId)
    if(chatIndex < 0) return
    chatList.value[chatIndex].customer_unread_messages_count = 0
    chatList.value[chatIndex].unread_messages_count = 0
}

function handleBack(){
    console.log(router.options.history)
    /* if(router.options.history.state.back){
        router.back()
    } else {
        navigateTo({ name: 'office' })
    } */
}
</script>

<template>
    <div class="min-h-screen">
        <!-- <LoaderLottieAnimation /> --> <!-- Анимация загрузки (Lottie) -->
        <Header />

        <div class="container h-full mx-auto px-4 lg:min-h-[950px] lg:mb-0 !mb-0">
            <div class="lg:mx-3 flex justify-start flex-wrap md:flex-nowrap !my-4">
                <div class="md:block hidden ys-profile-menu w-full md:w-[230px] lg:w-[272px]">
                    <div class="ys-profile-logo lg:p-3">
                        <UserProfileCard 
                            :username="userStore.user?.username || ''"
                            :role="userStore.user?.user_role || ''"
                            :email="userStore.user?.email || ''"
                            :photo="userStore.user?.avatar?.url || ''"
                            @click="navigateTo({ name: 'office' });" />
                    </div>
                    <div class="ys-profile-menu-list">
                        <div v-for="item in userStore.user?.user_roles">
                            <div class="ys-master-mode-box mb-5 flex items-center justify-between"
                                v-if="item === RoleEnum.MASTER">
                                {{ $t("Specialist mode") }}
                                <MasterModeSwitcher />
                            </div>
                        </div>
                        <div class="list-title">{{ $t("MAIN") }}</div>
                        <template v-for="item in tabList">
                            <NuxtLink v-if="item.show_in_desktop"
                                class="ys-profile-menu-item flex items-center my-1 justify-between"
                                :class="{ current: isCurrentRoute(item) }" :to="item.path">
                                <span class="flex items-center">
                                    <Icon v-if="item.icon" size="18" class="menu-item-icon ltr:mr-1 rtl:ml-1"
                                        :class="{ 'text-primary-500': isCurrentRoute(item) }" :name="item.icon" />
                                    {{ $t(item.name) }}
                                </span>
                                <span class="flex items-center">
                                    <span class="new-messages-icon"
                                        v-if="item.name === 'Chats' && userStore.unreadResponsesCount">{{
                                        userStore.unreadResponsesCount }}</span>
                                    <Icon v-if="isCurrentRoute(item)" size="24"
                                        class="text-gray-500 rtl:rotate-180 ltr:ml-2 rtl:mr-2"
                                        name="ri:arrow-drop-right-line" />
                                </span>
                            </NuxtLink>
                        </template>
                        <!-- <NuxtLink v-if="userProfile.Role === 'MASTER'" to="/master-office/promotion">
                            <PagesOfficeMasterMenuPromoPlaque />
                        </NuxtLink> -->
                    </div>


                </div>
                <div class="xl:ltr:border-l xl:rtl:border-r xl:border-gray-200 xl:pl-6 grow w-full">
                    <div class="wrapper h-full">
                        <ClientOnly >
                            <div class="ys-block-info h-full">
                                <div class="mobile_header md:hidden grid grid-cols-6 items-center mb-6">
                                    <div class="col-span-1">
                                        <!-- <Button @click.prevent="handleBack" variant="ghost" class="p-0">
                                            <Icon name="ri:arrow-left-line" size="24px" />
                                        </Button> -->
                                    </div>
                                    <div class="col-span-4">
                                        <p class="text-center font-medium uppercase text-[13px] tracking-widest leading-none">{{ $t('Chats') }}</p>
                                        <p class="text-center text-[10px] text-gray-400">{{$t('Discussions')}}</p>
                                    </div>
                                    <div class="col-span-1">
                                    </div>
                                </div>
                                <div v-if="sortedChats?.length" class="md:border border-gray-200 md:rounded-2xl overflow-hidden md:min-h-[670px] md:h-[70vh] flex justify-between h-full" ref="chatContainer">
                                    <div class="rooms_list w-full md:w-max-[270px] md:w-[160px] lg:w-[240px] xl:w-[290px] shrink-0 md:ltr:border-r border-gray-200 md:rtl:border-l">
                                        <div class="hidden md:flex items-center border-b border-gray-200 h-[64.5px] py-4 md:px-4 leading-0">
                                            <div class="font-medium text-[14px] lg:text-[16px]">
                                                <Icon size="24" class="ltr:mr-2 rtl:ml-2 text-zinc-600" name="ri:contacts-line" />
                                                {{ $t("Discussions") }}
                                            </div>
                                        </div>
                                        <RoomsList 
                                            :chats="sortedChats" 
                                            :is-loading-more="isLoadingMore"
                                            :has-more="hasMoreChats"
                                            @load-more="loadMoreChats"
                                            @setViewedMessages="setViewedMessages"
                                            class="h-full px-2 py-1 pb-28 lg:pb-3" 
                                            :style="!isMobile ? {height: chatContainer?.clientHeight ? chatContainer?.clientHeight - 50 + 'px' : '550px'} : null" 
                                        />
                                    </div>

                                    <NuxtPage />
                                    
                                </div>
                                <div v-else class="ys-block-inf-img flex flex-col items-center justify-center w-full h-full">
                                    <NuxtImg width="148" src="MyOffice/Empty-States-3.png" />
                                    <p class="ys-home-text text-center mb-4">{{ $t("You don't have dialogs yet") }}</p>
                                    <NuxtLink :to="{ name: userStore.user?.user_role === RoleEnum.MASTER ? 'office_task_search' : 'order_create' }">
                                        <Button class="ys-button ys-profile-button" style="width: 159px">
                                            <Icon size="18" class="menu-item-icon mr-1" name="ri:add-line" />
                                            {{ $t(userStore.user?.user_role === RoleEnum.MASTER ? "Task Search" : "Create Order") }}
                                        </Button>
                                    </NuxtLink>
                                </div>
                            </div>
                        </ClientOnly>
                    </div>
                </div>
            </div>
        </div>

        <Footer />
        <BottomBar />
    </div>
</template>

<style scoped lang="scss">

.ys-home-text {
    font-family: Involve, sans-serif;
    font-size: 34px;
    line-height: 45px;
}

.ys-profile-logo {
    height: 88px;
    gap: 10px;
}


.ys-profile-menu {
    gap: 0;

    .ys-profile-menu-list {
        // height: 222px;
        width: 100%;
        padding: 20px 20px 16px 20px;
        gap: 20px;

        @media(max-width:1024px) {
            padding: 0;
        }

        .list-title {
            height: 24px;
            padding: 4px;
            gap: 8px;
            font-size: 12px;
            font-weight: 500;
            line-height: 16px;
            letter-spacing: 0.04em;
            color: rgba(134, 140, 152, 1);
        }

        .ys-profile-menu-item {
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;
            line-height: 20px;
            letter-spacing: -0.006em;
            color: rgba(82, 88, 102, 1);
            height: 36px;
            padding: 8px 12px 8px 12px;
            gap: 8px;
            border-radius: 8px;

            &:hover {
                color: rgba(10, 13, 20, 1);
                background: rgba(246, 248, 250, 1);
            }

            &:active {
                color: rgba(10, 13, 20, 1);
                background: rgba(246, 248, 250, 1);
            }
        }

        .current {
            color: rgba(10, 13, 20, 1);
            background: rgba(246, 248, 250, 1);

            .menu-item-icon {
                color: rgba(45, 159, 117, 1);
            }
        }
    }
}

.ys-master-mode-box {
    width: 100%;
    height: 36px;
    padding: 8px;
    gap: 8px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: -0.006em;
    text-align: left;
    color: rgba(10, 13, 20, 1);
}
</style>